import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Analytics from "@/components/Analytics";
import { useAuth } from "@/context/AuthContext";
import { LanguageProvider, stripLocale } from "@/context/LanguageContext";
import { DEFAULT_LOCALE, PREFIXED_LOCALES } from "@/i18n/locales";

// Home is eager (landing → fastest LCP). Everything else is code-split so the
// initial bundle stays small (heavy Admin/Dashboards load on demand).
import Home from "@/pages/Home";
// Non-critical floating widgets — split out of the main bundle so they don't
// add to the critical main-thread parse (protects interactivity / TBT).
const ConciergeWidget = lazy(() => import("@/components/ConciergeWidget"));
const ContactWidget = lazy(() => import("@/components/ContactWidget"));
const ConsentBanner = lazy(() => import("@/components/ConsentBanner"));
const Search = lazy(() => import("@/pages/Search"));
const VillaDetail = lazy(() => import("@/pages/VillaDetail"));
const AreaPage = lazy(() => import("@/pages/AreaPage"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
const SeoCatchAll = lazy(() => import("@/components/SeoCatchAll"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Login = lazy(() => import("@/pages/Login"));
const BookingStatus = lazy(() => import("@/pages/BookingStatus"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const OwnerDashboard = lazy(() => import("@/pages/OwnerDashboard"));
const Admin = lazy(() => import("@/pages/admin/Admin"));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
    <div className="h-9 w-9 rounded-full border-2 border-[var(--lux-stone)] border-t-[var(--lux-seaglass)] animate-spin" />
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const SiteLayout = ({ children }) => {
  const { pathname } = useLocation();
  const isHome = stripLocale(pathname) === "/";
  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <main className={`flex-1 ${isHome ? "" : "pt-[68px]"}`}>
        <Suspense fallback={<PageLoader />}>{children || <Outlet />}</Suspense>
      </main>
      <Footer />
      <Suspense fallback={null}>
        <ConciergeWidget />
        <ContactWidget />
      </Suspense>
    </div>
  );
};

// Validates the :locale segment; redirects /en/* -> /* and 404s unknown prefixes.
const LocaleGate = () => {
  const { locale } = useParams();
  const location = useLocation();
  if (locale === DEFAULT_LOCALE) {
    return <Navigate to={stripLocale(location.pathname) + location.search} replace />;
  }
  if (!PREFIXED_LOCALES.includes(locale)) {
    return <SiteLayout><SeoCatchAll /></SiteLayout>;
  }
  return <SiteLayout />;
};

// Public, localizable pages (relative paths => work at root and under /:locale).
const publicRoutes = () => (
  <>
    <Route index element={<Home />} />
    <Route path="search" element={<Search />} />
    <Route path="villas/:slug" element={<VillaDetail />} />
    <Route path="areas/:slug" element={<AreaPage />} />
    <Route path="collections/:slug" element={<CollectionPage />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="about" element={<About />} />
    <Route path="contact" element={<Contact />} />
    <Route path="booking-status" element={<BookingStatus />} />
    {/* Root-level programmatic SEO landing pages (e.g. /beachfront-villas-puerto-banus) */}
    <Route path="*" element={<SeoCatchAll />} />
  </>
);

const Protected = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && !(user.role === role || user.role === "admin" || user.role === "manager")) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ScrollToTop />
        <Analytics />
        <Toaster position="top-center" richColors />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* English (default) at root */}
            <Route element={<SiteLayout />}>{publicRoutes()}</Route>
            {/* Locale-prefixed public pages */}
            <Route path=":locale" element={<LocaleGate />}>{publicRoutes()}</Route>

            {/* Non-localized app routes */}
            <Route path="/login" element={<SiteLayout><Login /></SiteLayout>} />
            <Route path="/register" element={<SiteLayout><Register /></SiteLayout>} />
            <Route path="/dashboard" element={<SiteLayout><Protected><Dashboard /></Protected></SiteLayout>} />
            <Route path="/owner" element={<SiteLayout><Protected role="owner"><OwnerDashboard /></Protected></SiteLayout>} />
            <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
          </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <ConsentBanner />
        </Suspense>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
